import { brandingSchema, DEFAULT_BRANDING, type Branding } from '@lpa/protocol';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { brandingStylesheet } from './stylesheet.js';

/**
 * The app's branding, loaded once and applied to everything (D-30).
 *
 * Wrapped around the whole router rather than fetched per page, because every
 * surface is branded -- the join screen a student opens with no account, the
 * projector board, `/leika`, the admin. One fetch, one `<style>` element.
 *
 * **It never blocks a render.** The shipped palette is compiled into
 * `global.css`, so the first paint is correct for an unbranded install and
 * merely un-recoloured for a branded one; gating the app on a network round
 * trip would trade a repaint for a blank screen, and the blank screen is worse
 * on the classroom wifi this runs on. A failed fetch is not an error state
 * either -- an app that will not open because it could not find out what colour
 * to be is a worse app than one that opens in blue.
 */

interface BrandingValue {
  branding: Branding;
  /** False until the first fetch settles, either way. */
  loaded: boolean;
  /** Applies a branding immediately, for the settings screen's live preview. */
  preview: (next: Branding | null) => void;
  /** Adopts a saved branding as the real one, ending any preview. */
  commit: (next: Branding) => void;
}

const BrandingContext = createContext<BrandingValue>({
  branding: DEFAULT_BRANDING,
  loaded: false,
  preview: () => undefined,
  commit: () => undefined,
});

const STYLE_ID = 'lpa-branding';

/**
 * Writes the generated CSS into the document.
 *
 * Appended to the end of `<head>`, which is what makes it win: it redefines the
 * same custom properties on the same `:root` selector as `global.css`, so the
 * two have equal specificity and the later one takes effect. Reusing a single
 * element by id means a preview keystroke replaces the sheet rather than
 * stacking a hundred of them.
 */
function applyStylesheet(css: string): void {
  let style = document.getElementById(STYLE_ID);

  if (style === null) {
    style = document.createElement('style');
    style.id = STYLE_ID;
  }

  /*
   * Kept last in the head, not merely put there once.
   *
   * This sheet redefines the same custom properties on the same `:root`
   * selector as `global.css`, so the two have equal specificity and *order* is
   * the entire tie-break. Appending once is not enough: every route is lazily
   * imported, and each one's stylesheet is injected into the head when it
   * loads -- after this element, if this element was appended at mount. Today
   * none of those files sets a palette token, so nothing is actually being
   * overridden; re-appending costs one DOM move and means it never can be.
   */
  if (style !== document.head.lastElementChild) document.head.append(style);

  style.textContent = css;
}

export function BrandingProvider({ children }: { children: ReactNode }): ReactElement {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [previewed, setPreviewed] = useState<Branding | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch('/api/branding', { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        return (await response.json()) as { branding: unknown };
      })
      .then((body) => {
        if (cancelled) return;
        // Parsed, not trusted: an older API, a proxy error page, or a partial
        // record should leave the app on its shipped palette rather than
        // feeding `undefined` into the stylesheet writer.
        const parsed = brandingSchema.safeParse(body.branding);
        if (parsed.success) setBranding(parsed.data);
      })
      .catch(() => {
        // Deliberately silent. See the note above: no branding is not an error
        // a learner can act on, and the app is fully usable without it.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const active = previewed ?? branding;

  const { css } = useMemo(() => brandingStylesheet(active), [active]);

  useEffect(() => {
    applyStylesheet(css);
  }, [css]);

  // The name in the tab, which is as much part of a white label as the logo.
  useEffect(() => {
    if (active.appName !== null && active.appName !== '') document.title = active.appName;
  }, [active.appName]);

  const commit = useCallback((next: Branding) => {
    setBranding(next);
    setPreviewed(null);
  }, []);

  const value = useMemo<BrandingValue>(
    () => ({ branding: active, loaded, preview: setPreviewed, commit }),
    [active, loaded, commit],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  return useContext(BrandingContext);
}
